/**
 * Appfolio PMS Transformer
 * Transforms Appfolio CSV data to our contact/property models
 */

import type {
  ContactTransformer,
  CSVRow,
  AppfolioContactRow,
  ContactUploadContext,
  ValidationResult,
} from '@/types/csv-upload'
import {
  RelationshipAccessLevel,
  ContactPropertyRelationshipRecord,
  IntegrationSource,
} from '@super/shared-types'
import {
  collectAppfolioPhones,
  parseAppfolioEmails,
  getPrimaryPhone,
  getAlternatePhones,
  getPrimaryEmail,
  getAlternateEmails,
  parseCombinedName,
  extractPropertyCompositeFromColumn,
} from './appfolio-utils'

// AppFolio CSV column definitions (UI: which headers are highlighted as required).
// First/last name are optional — many third-party exports omit one or both.
export const APPFOLIO_REQUIRED_FIELDS = [
  'Emails',
  'Phone Numbers',
  'Tenant ID',
  'Owner ID',
  'Vendor ID',
  'Manager ID',
  'Property ID',
  'Unit ID',
  'Status',
] as const
export const APPFOLIO_COMMON_FIELDS = [
  'First Name',
  'Last Name',
  'Emails',
  'Phone Numbers',
  'Company Name',
  'Property ID',
  'Status',
] as const
export const APPFOLIO_ID_FIELDS = [
  'Tenant ID',
  'Owner ID',
  'Vendor ID',
  'Manager ID',
] as const

export const appfolioTransformer: ContactTransformer = {
  providerName: IntegrationSource.APPFOLIO,

  transformContact(
    row: CSVRow,
    context: ContactUploadContext,
  ):
    | ContactPropertyRelationshipRecord[]
    | ContactPropertyRelationshipRecord
    | null {
    const appfolioRow = row as AppfolioContactRow

    // Step 1: Prefer "First Name" and "Last Name" if available
    let firstName = appfolioRow['First Name']?.trim()
    let lastName = appfolioRow['Last Name']?.trim()

    // Step 2: Try to parse combined name column based on relationship type
    if (!firstName || !lastName) {
      const relationshipType = context.relationshipType
      let combinedNameField: string | undefined

      if (relationshipType === RelationshipAccessLevel.TENANT) {
        combinedNameField = appfolioRow.Tenant
      } else if (relationshipType === RelationshipAccessLevel.OWNER) {
        combinedNameField = appfolioRow.Owner
      } else if (relationshipType === RelationshipAccessLevel.VENDOR) {
        combinedNameField = appfolioRow.Vendor
      } else if (relationshipType === RelationshipAccessLevel.MANAGER) {
        combinedNameField = appfolioRow.Manager
      }

      if (combinedNameField) {
        const parsedName = parseCombinedName(combinedNameField)
        if (parsedName) {
          // Only use parsed values if original fields are missing
          if (!firstName && parsedName.firstName) {
            firstName = parsedName.firstName
          }
          if (!lastName && parsedName.lastName) {
            lastName = parsedName.lastName
          }
        }
      }
    }

    // Step 3: Get Company Name for aliases mapping
    const companyName = appfolioRow['Company Name']?.trim()
    const aliases = companyName ? [companyName] : undefined

    // Step 4: Validation - no longer strictly required, but will warn if all are missing
    // Allow transformation to proceed even without firstName/lastName
    // The validation step will handle warnings

    const phoneNumbers = collectAppfolioPhones(appfolioRow)
    const primary = getPrimaryPhone(phoneNumbers)
    const alternatePhones = getAlternatePhones(phoneNumbers, primary.number)

    // Parse emails from the field
    // Owner sheets use 'Email' (singular), tenant sheets use 'Emails' (plural)
    const emailField = appfolioRow.Email || appfolioRow.Emails
    const emails = parseAppfolioEmails(emailField)
    const primaryEmail = getPrimaryEmail(emails)
    const alternateEmails = getAlternateEmails(emails, primaryEmail)

    // Get external ID based on relationship type from context
    let externalId: string | undefined
    const relationshipType = context.relationshipType

    if (relationshipType === RelationshipAccessLevel.TENANT) {
      externalId = appfolioRow['Tenant ID']?.trim()
    } else if (relationshipType === RelationshipAccessLevel.OWNER) {
      externalId = appfolioRow['Owner ID']?.trim()
    } else if (relationshipType === RelationshipAccessLevel.VENDOR) {
      externalId = appfolioRow['Vendor ID']?.trim()
    } else if (relationshipType === RelationshipAccessLevel.MANAGER) {
      externalId = appfolioRow['Manager ID']?.trim()
    }

    // Parse Status field to determine isActive
    const status = appfolioRow.Status?.trim()?.toLowerCase()
    const isActive = status === 'current'

    // Parse Name field (full name used for non-primary contacts/aliases)
    const nameField = appfolioRow.Name?.trim() || undefined

    // Build base contact data (shared across all properties)
    const baseContact = {
      name: nameField,
      firstName: firstName || undefined,
      lastName: lastName || undefined,
      companyName: appfolioRow['Company Name']?.trim() || undefined,
      aliases,
      primaryEmail,
      primaryPhoneNumber: primary.number,
      primaryPhoneNumberType: primary.type,
      alternateEmails: alternateEmails.length > 0 ? alternateEmails : undefined,
      alternatePhoneNumbers:
        alternatePhones.length > 0 ? alternatePhones : undefined,
      externalId, // Map the external ID from Owner ID/Tenant ID/etc
      city: appfolioRow.City?.trim() || undefined,
      isActive,
    }

    // Handle property IDs based on relationship type
    if (relationshipType === RelationshipAccessLevel.OWNER) {
      // Owners can have multiple properties (comma-separated)
      const propertiesOwnedIds = appfolioRow['Properties Owned IDs']?.trim()

      if (!propertiesOwnedIds) {
        // No properties - return single record with no property link
        return {
          ...baseContact,
          relationshipExternalId: externalId
            ? `${relationshipType.toLowerCase()}-${externalId}`
            : undefined,
          propertyExternalId: undefined,
          unitExternalId: undefined,
        }
      }

      // Split comma-separated property IDs and create one record per property
      const propertyIds = propertiesOwnedIds
        .split(',')
        .map(id => id.trim())
        .filter(id => id.length > 0)

      if (propertyIds.length === 0) {
        // Empty after filtering - return single record
        return {
          ...baseContact,
          relationshipExternalId: externalId
            ? `${relationshipType.toLowerCase()}-${externalId}`
            : undefined,
          propertyExternalId: undefined,
          unitExternalId: undefined,
        }
      }

      // Return array of records, one for each property
      return propertyIds.map(propertyId => ({
        ...baseContact,
        // Create unique relationship ID for each property
        relationshipExternalId: externalId
          ? `${relationshipType.toLowerCase()}-${externalId}-${propertyId}`
          : undefined,
        propertyExternalId: propertyId,
        unitExternalId: undefined, // Owners don't have units
      }))
    } else {
      // Tenants, vendors, managers have single property
      // propertyExternalId: numeric Property ID when present, otherwise a synthetic composite extracted from the Property column
      const rawProperty = appfolioRow['Property']?.trim()
      const propertyExternalId =
        appfolioRow['Property ID']?.trim() ||
        (rawProperty
          ? extractPropertyCompositeFromColumn(rawProperty)
          : undefined) ||
        undefined
      // unitExternalId: numeric Unit ID when present, otherwise derive a Property::Unit composite
      const unitCol = appfolioRow['Unit']?.trim()
      const unitExternalId =
        appfolioRow['Unit ID']?.trim() ||
        (propertyExternalId && unitCol
          ? `${propertyExternalId}::${unitCol}`
          : undefined)
      return {
        ...baseContact,
        relationshipExternalId: externalId
          ? `${relationshipType.toLowerCase()}-${externalId}`
          : undefined,
        propertyExternalId,
        unitExternalId,
      }
    }
  },

  validateContact(
    record: ContactPropertyRelationshipRecord,
    _context: ContactUploadContext,
  ): ValidationResult {
    const errors: string[] = []
    const warnings: string[] = []

    // Name field validation - warn if all identifying fields are missing
    const hasFirstName = !!record.firstName
    const hasLastName = !!record.lastName
    const hasAliases = !!record.aliases && record.aliases.length > 0

    if (!hasFirstName && !hasLastName && !hasAliases) {
      warnings.push(
        'Row does not have a "First Name", "Last Name", or "Aliases" (Company Name) - consider adding identifying information',
      )
    }

    // Contact information validation
    if (!record.primaryEmail && !record.primaryPhoneNumber) {
      warnings.push(
        'No primary contact method (email or phone) provided - consider adding contact information',
      )
    }

    // Phone number validation - primary phone
    if (record.primaryPhoneNumber) {
      if (!record.primaryPhoneNumber.startsWith('+1')) {
        errors.push(
          'Primary phone number must be in E.164 format with +1 country code - check "Phone Numbers" column format',
        )
      } else if (record.primaryPhoneNumber.length !== 12) {
        errors.push(
          `Primary phone number must be exactly 12 characters (+1 followed by 10 digits), got ${record.primaryPhoneNumber.length} characters - check "Phone Numbers" column format`,
        )
      }
    }

    // Phone number validation - alternate phones
    if (
      record.alternatePhoneNumbers &&
      record.alternatePhoneNumbers.length > 0
    ) {
      record.alternatePhoneNumbers.forEach((phone, index) => {
        const phoneNumber = typeof phone === 'string' ? phone : phone.number
        if (phoneNumber) {
          if (!phoneNumber.startsWith('+1')) {
            errors.push(
              `Alternate phone number ${index + 1} must be in E.164 format with +1 country code - check "Phone Numbers" column format`,
            )
          } else if (phoneNumber.length !== 12) {
            errors.push(
              `Alternate phone number ${index + 1} must be exactly 12 characters (+1 followed by 10 digits), got ${phoneNumber.length} characters - check "Phone Numbers" column format`,
            )
          }
        }
      })
    }

    // Email validation (basic)
    if (record.primaryEmail && !record.primaryEmail.includes('@')) {
      errors.push(
        'Primary email appears to be invalid - check "Emails" column format',
      )
    }

    return {
      isValid: errors.length === 0,
      errors,
      warnings,
    }
  },
}
